import type { Board } from "../board";
import { PRIORITY_LAST, type ProcessableThing } from "../interfaces";
import { Thing } from "../thing";

/**
 * Non-physical thing that emits a hint after a fixed delay of player turns, then removes
 * itself. Drop one into a level generator to schedule a hint on level arrival. Engine-side
 * dedup at `Game.emitHint` means subsequent runs that re-spawn the issuer for an already-seen
 * hint will silently tick down and self-remove.
 */
export class ScheduledHint extends Thing implements ProcessableThing {
	static key = "ScheduledHint";
	static readonly DELAY_TURNS = 2;

	private hintId: string;
	private hintStringKey: string;
	private turnsRemaining: number;

	constructor(hint: { id: string; stringKey: string }) {
		super();
		this.hintId = hint.id;
		this.hintStringKey = hint.stringKey;
		this.turnsRemaining = ScheduledHint.DELAY_TURNS;
	}

	getProcessPriority(): number {
		return PRIORITY_LAST;
	}

	processTurn(board: Board): void {
		this.turnsRemaining--;
		if (this.turnsRemaining > 0) return;
		board.game?.emitHint({ id: this.hintId, stringKey: this.hintStringKey });
		board.removeAtEndOfTurn(this);
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			hintId: this.hintId,
			hintStringKey: this.hintStringKey,
			turnsRemaining: this.turnsRemaining,
		};
	}

	static deserialize(data: any): ScheduledHint {
		const t = new ScheduledHint({ id: data.hintId, stringKey: data.hintStringKey });
		t.turnsRemaining = data.turnsRemaining ?? ScheduledHint.DELAY_TURNS;
		return t;
	}
}
